import { type Dashboard } from '@lightdash/common';
import { Box, Button, Group, Portal, Stack, Text, Textarea, Title } from '@mantine/core';
import { useCallback, useEffect, useState, type FC } from 'react';
import { Link } from 'react-router';
import MantineModal from '../../components/common/MantineModal';
import { useDashboardRefresh } from '../../hooks/dashboard/useDashboardRefresh';
import useDashboardTileStatusContext from '../../providers/Dashboard/useDashboardTileStatusContext';
import useDashboardContext from '../../providers/Dashboard/useDashboardContext';
import useApp from '../../providers/App/useApp';
import AssumptionSetBar from '../assumptionSets/AssumptionSetBar';
import { ParameterInput } from '../parameters/components/ParameterInput';
import classes from './lookthrough.module.css';
import { isStaff, LOOKTHROUGH, useLookthroughContext, type LookthroughPage } from './useLookthroughContext';

type Props = {
    dashboard: Dashboard;
    page: LookthroughPage;
    onExport: () => void;
    onEditClicked: () => void;
    isEditMode: boolean;
};

/**
 * Run: compute the page's numbers again instead of reading the stored ones (our results cache).
 * One overlay over the page while it computes, so it reads as "new numbers coming", not a slow page (E9).
 */
const RunButton: FC = () => {
    const { isFetching, invalidateDashboardRelatedQueries, invalidateDashboardResultsQueries, removeDashboardResultsQueries } =
        useDashboardRefresh();
    const clearCacheAndFetch = useDashboardTileStatusContext((c) => c.clearCacheAndFetch);
    const [running, setRunning] = useState(false);
    // the overlay stays until every tile of the page has its new numbers (no tile loader left)
    useEffect(() => {
        if (!running) return undefined;
        const started = Date.now();
        const t = setInterval(() => {
            const busy = isFetching > 0 || document.querySelector(
                '.react-grid-item .mantine-Loader-root, .react-grid-item .mantine-Skeleton-root');
            if (!busy && Date.now() - started > 1500) setRunning(false);
        }, 500);
        return () => clearInterval(t);
    }, [running, isFetching]);
    const run = useCallback(async () => {
        setRunning(true);
        removeDashboardResultsQueries();
        clearCacheAndFetch();
        await invalidateDashboardRelatedQueries();
        await invalidateDashboardResultsQueries();
    }, [removeDashboardResultsQueries, clearCacheAndFetch, invalidateDashboardRelatedQueries, invalidateDashboardResultsQueries]);
    return (
        <>
            <Button className={classes.btn} variant="default" onClick={run} disabled={!!running}>
                Run
            </Button>
            {running && (
                <Portal>
                    <Box className={classes.computeOverlay} role="status" data-testid="lookthrough-computing">
                        <Box className={classes.computeCard}>
                            <Box className={classes.computeRings} />
                            <Text className={classes.computeText}>Computing new numbers…</Text>
                            <Text className={classes.computeSub}>every tile of this page, from the tape on screen</Text>
                        </Box>
                    </Box>
                </Portal>
            )}
        </>
    );
};

/** A request to QueensGiant that visibly goes out: prefilled, then email or copy (E16, D.1). */
export const AskDialog: FC<{ opened: boolean; onClose: () => void; subject: string; body: string }> = ({
    opened,
    onClose,
    subject,
    body,
}) => {
    const [text, setText] = useState(body);
    const [copied, setCopied] = useState(false);
    useEffect(() => {
        if (opened) {
            setText(body);
            setCopied(false);
        }
    }, [opened, body]);
    const href = `mailto:${LOOKTHROUGH.contact}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(text)}`;
    return (
        <MantineModal opened={opened} onClose={onClose} title={subject} size="lg">
            <Stack gap="xs" data-testid="lookthrough-ask">
                <Text size="sm">To QueensGiant ({LOOKTHROUGH.contact}). Say what you need; the deal and page are filled in.</Text>
                <Textarea autosize minRows={5} value={text} onChange={(e) => setText(e.currentTarget.value)} />
                <Group justify="flex-end">
                    <Button
                        variant="default"
                        onClick={() => {
                            void navigator.clipboard?.writeText(`${subject}\n\n${text}`);
                            setCopied(true);
                        }}
                    >
                        {copied ? 'Copied' : 'Copy'}
                    </Button>
                    <Button component="a" href={href}>
                        Send by email
                    </Button>
                </Group>
            </Stack>
        </MantineModal>
    );
};

/**
 * Phil's page header on LOOKTHROUGH pages (spec 7.3 G5, D.1, E2-E4): serif title, the deal, Portfolio,
 * As of, the page's assumptions as labelled controls, and the page's actions from pages.json: Export
 * (print to PDF), open another page, or ask QueensGiant. Staff also get Run and Publish / Approve.
 */
const LookthroughPageHeader: FC<Props> = ({ dashboard, page, onEditClicked, isEditMode }) => {
    const { user } = useApp();
    const { projectUuid, context } = useLookthroughContext();
    const filters = useDashboardContext((c) => c.allFilters);
    const definitions = useDashboardContext((c) => c.parameterDefinitions);
    const values = useDashboardContext((c) => c.parameterValues);
    const setParameter = useDashboardContext((c) => c.setParameter);
    const [ask, setAsk] = useState<string | null>(null);
    const dealId = filters?.dimensions?.find((d) => d.target.fieldId.endsWith('_deal_id'))?.values?.[0];
    const dealName = typeof dealId === 'string' ? LOOKTHROUGH.deals[dealId] ?? dealId : 'All deals';
    const staff = isStaff(user.data?.role); // a client may manage dashboards in its own space; that is not staff
    const input = (key: string, label: string) =>
        definitions?.[key] ? (
            <Group key={key} className={classes.control} gap={6} wrap="nowrap">
                {label}
                <Box className={classes.controlInput}>
                    <ParameterInput
                        paramKey={key}
                        parameter={definitions[key]}
                        value={values?.[key] ?? null}
                        onParameterChange={setParameter}
                        size="xs"
                        projectUuid={dashboard.projectUuid}
                        parameterValues={values}
                    />
                </Box>
            </Group>
        ) : null;
    if (isEditMode) return null; // editing uses Lightdash's own header (admins)
    return (
        <Stack gap={0} className={classes.pageHead} data-testid="lookthrough-page-header">
            <Group align="flex-start" wrap="nowrap">
                <Box>
                    <Title order={1} className={classes.pageTitle}>
                        {page.name}
                    </Title>
                    <Text className={classes.pageSub}>{page.about ?? dealName}</Text>
                </Box>
                <Group className={classes.headActions} gap={8} wrap="nowrap">
                    <Box className={classes.control}>
                        Portfolio&nbsp; <b>{dealName}</b>
                    </Box>
                    {input('snapshot', 'As of')}
                    {page.actions.map((a) =>
                        a.does === 'export' ? (
                            <Button key={a.label} className={classes.btn} variant="default" onClick={() => window.print()}>
                                {a.label}
                            </Button>
                        ) : a.does === 'open_page' ? (
                            <Button
                                key={a.label}
                                component={Link}
                                to={`/projects/${projectUuid}/dashboards/${a.page}/view${context}`}
                                className={classes.btnPrimary}
                                variant="default"
                            >
                                {a.label}
                            </Button>
                        ) : a.does === 'mail' ? (
                            <Button key={a.label} className={classes.btn} variant="default" onClick={() => setAsk(a.subject ?? a.label)}>
                                {a.label}
                            </Button>
                        ) : null, // upload: not in Release 1 (D.1)
                    )}
                    {staff && <RunButton />}
                    {user.data?.role === 'admin' && ( // G5: admins keep Lightdash's editor
                        <Button className={classes.btn} variant="subtle" onClick={onEditClicked}>
                            Open in Lightdash
                        </Button>
                    )}
                </Group>
            </Group>
            {!!page.controls?.length && (
                <Group className={classes.assumptions} gap={8} data-testid="lookthrough-assumptions">
                    <Text className={classes.assumptionsLabel}>Assumptions</Text>
                    {page.controls.map((c) => input(c.input, c.label))}
                </Group>
            )}
            {staff && page.assumptions && projectUuid && (
                // Publish / Approve stay one click away so the page's numbers lead (design review)
                <Box component="details" className={classes.publish}>
                    <Box component="summary" className={classes.publishSummary}>Publish / Approve</Box>
                    <AssumptionSetBar projectUuid={projectUuid} module={page.assumptions} />
                </Box>
            )}
            <AskDialog
                opened={ask !== null}
                onClose={() => setAsk(null)}
                subject={`${dealName} · ${page.name} · ${ask ?? ''}`}
                body={`Hello QueensGiant,\n\nOn the ${page.name} page of ${dealName}, I would like: \n\n`}
            />
        </Stack>
    );
};

export default LookthroughPageHeader;
